import { memo, useEffect, useRef, useState } from 'react';

/**
 * Fixed point overlay that always renders as a circle regardless of figure aspect ratio.
 * @param {{ axis, x, y, color, color1, strokeWidth, size, yIsUp }} props
 */
function IntFigPoint({
  axis,
  x: px, y: py,
  color = '#7fffd4',
  color1 = '#000000',
  strokeWidth = 0.01,
  size = 0.1,
  yIsUp = false,
}) {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { width, height } = containerSize;

  if (width === 0 || height === 0) {
    return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
  }

  const xFrac = (px - axis.min_x) / (axis.max_x - axis.min_x);
  const yFrac = yIsUp
    ? 1 - (py - axis.min_y) / (axis.max_y - axis.min_y)
    : (py - axis.min_y) / (axis.max_y - axis.min_y);

  // Use min dimension so the circle stays circular at any aspect ratio.
  const minDim = Math.min(width, height);
  const radiusPx = size * minDim;
  const diameterPx = radiusPx * 2;
  const strokePx = strokeWidth * minDim;

  const leftPx = xFrac * width - radiusPx;
  const topPx = yFrac * height - radiusPx;

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div
        style={{
          position: 'absolute',
          left: leftPx,
          top: topPx,
          width: diameterPx,
          height: diameterPx,
          borderRadius: '50%',
          backgroundColor: color,
          border: `${strokePx}px solid ${color1}`,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

export default memo(IntFigPoint);
